// Lista de fluxos, chave geral e painel de execução — Fases 10 e 11.
//
// É a porta da automação. Três coisas moram aqui, e as três precisam ser
// achadas por alguém com pressa:
//
//  1. A CHAVE GERAL. O botão que desliga a automação da empresa inteira. Fica
//     no topo, sempre visível, e o efeito é imediato — o motor relê essa linha
//     a cada passo, sem deploy.
//  2. QUAIS FLUXOS ESTÃO LIGADOS, e o que cada um andou fazendo.
//  3. ONDE OS RUNS PARARAM MAL. Falha e interrupção aparecem primeiro; sucesso
//     não precisa de atenção.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Loader2, Plus, Power, RefreshCw, ShieldAlert,
  Trash2, Workflow, Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useInboxMembro } from '../../../src/hooks/useInboxMembro';
import { carregarOrgId } from '../Inbox/api';
import { desdeQuando } from '../Inbox/formato';
import type { Fluxo } from './tipos';
import { apagarFluxo, carregarFluxos, criarFluxo, ligarFluxo } from './apiFluxos';
import type { ResumoRuns } from './apiGatilhos';
import {
  carregarResumoRuns, carregarRunsProblematicos, lerMotorGeral, ligarMotorGeral, pararRunsDoFluxo,
} from './apiGatilhos';
import type { Run } from './tipos';

export default function FluxosView() {
  const { profile } = useAuth();
  const { ehAdmin, carregando: carregandoAcesso } = useInboxMembro(profile?.id);

  const [orgId, setOrgId] = useState<string | null>(null);
  const [fluxos, setFluxos] = useState<Fluxo[]>([]);
  const [resumo, setResumo] = useState<ResumoRuns[]>([]);
  const [problematicos, setProblematicos] = useState<Run[]>([]);
  const [motorLigado, setMotorLigado] = useState(true);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);

  const buscar = useCallback(async (org: string) => {
    setErro(null);
    try {
      const [f, r, p, m] = await Promise.all([
        carregarFluxos(org),
        carregarResumoRuns(org),
        carregarRunsProblematicos(org),
        lerMotorGeral(org),
      ]);
      setFluxos(f);
      setResumo(r);
      setProblematicos(p);
      setMotorLigado(m);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    let vivo = true;
    carregarOrgId(profile.id)
      .then((id) => {
        if (!vivo) return;
        setOrgId(id);
        if (id) void buscar(id);
        else setCarregando(false);
      })
      .catch((e) => vivo && setErro((e as Error).message));
    return () => { vivo = false; };
  }, [profile?.id, buscar]);

  const porFluxo = useMemo(() => new Map(resumo.map((r) => [r.flow_id, r])), [resumo]);
  const nomePorFluxo = useMemo(() => new Map(fluxos.map((f) => [f.id, f.nome])), [fluxos]);

  const criar = useCallback(async () => {
    if (!orgId || !nome.trim()) return;
    setOcupado('novo');
    try {
      await criarFluxo(orgId, nome.trim(), null);
      setNome('');
      setCriando(false);
      await buscar(orgId);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }, [buscar, nome, orgId]);

  /**
   * Ligar/desligar um fluxo.
   *
   * Desligar PERGUNTA se interrompe os runs em andamento. É o encontro de duas
   * decisões que parecem brigar e não brigam: o critério desta fase diz
   * "desativar interrompe os runs", e a Fase 9 decidiu que desligar e cancelar
   * são coisas diferentes. A pergunta resolve — quem desativa escolhe, em vez
   * de descobrir depois o que o sistema fez sozinho.
   */
  const alternar = useCallback(async (f: Fluxo) => {
    if (!orgId) return;
    const desligando = f.ativo;
    const emAndamento = (porFluxo.get(f.id)?.ativos ?? 0) + (porFluxo.get(f.id)?.aguardando ?? 0);

    let interromper = false;
    if (desligando && emAndamento > 0) {
      interromper = confirm(
        `${emAndamento} conversa(s) estão no meio deste fluxo agora.\n\n`
        + 'OK: desativa e interrompe essas conversas.\n'
        + 'Cancelar: desativa, mas deixa quem já começou terminar.',
      );
    }

    setOcupado(f.id);
    try {
      await ligarFluxo(f.id, !f.ativo);
      if (interromper) await pararRunsDoFluxo(f.id, 'O fluxo foi desativado.');
      await buscar(orgId);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }, [buscar, orgId, porFluxo]);

  const alternarMotor = useCallback(async () => {
    if (!orgId) return;
    if (motorLigado && !confirm(
      'Desligar a automação de TODA a organização?\n\n'
      + 'Nenhum fluxo dispara e os que estão andando param no próximo passo. '
      + 'O efeito é imediato, sem deploy.',
    )) return;

    setOcupado('motor');
    try {
      await ligarMotorGeral(orgId, !motorLigado);
      await buscar(orgId);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }, [buscar, motorLigado, orgId]);

  const remover = useCallback(async (f: Fluxo) => {
    if (!orgId) return;
    if (!confirm(`Apagar o fluxo "${f.nome}"? Isso não pode ser desfeito.`)) return;
    setOcupado(f.id);
    try {
      await apagarFluxo(f.id);
      await buscar(orgId);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }, [buscar, orgId]);

  if (carregandoAcesso || carregando) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (!ehAdmin) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
        <ShieldAlert className="mx-auto mb-3 text-gray-400" size={28} />
        <p className="font-semibold text-gray-800 dark:text-slate-100">Acesso restrito</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          Automação é visível só para administradores do Omnichannel — um fluxo escreve para o
          cliente sozinho.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 md:px-6">
      <header className="flex flex-wrap items-start justify-between gap-3 py-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Automação</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Fluxos que respondem sozinhos, e o que eles andaram fazendo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => orgId && void buscar(orgId)}
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300"
          >
            <RefreshCw size={13} />
            Atualizar
          </button>
          <button
            onClick={() => setCriando(true)}
            className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          >
            <Plus size={13} />
            Novo fluxo
          </button>
        </div>
      </header>

      {erro && (
        <div className="mb-3 rounded-xl bg-red-50 px-3.5 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {erro}
        </div>
      )}

      {/* ─── Chave geral ─────────────────────────────────────────────────── */}
      <section
        className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3 ring-1 ${
          motorLigado
            ? 'bg-white ring-gray-200 dark:bg-slate-800 dark:ring-slate-700'
            : 'bg-red-50 ring-red-200 dark:bg-red-900/20 dark:ring-red-900/40'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <span className={`rounded-xl p-2 ${motorLigado ? 'bg-primary/10 text-primary' : 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300'}`}>
            <Power size={16} />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-800 dark:text-white">
              {motorLigado ? 'Automação ligada' : 'Automação DESLIGADA'}
            </p>
            <p className="text-xs leading-snug text-gray-500 dark:text-slate-400">
              {motorLigado
                ? 'A chave geral. Desligar para tudo no próximo passo, sem precisar de deploy.'
                : 'Nenhum fluxo dispara e os que estavam andando pararam. Ligue quando o problema estiver resolvido.'}
            </p>
          </div>
        </div>
        <button
          onClick={() => void alternarMotor()}
          disabled={ocupado === 'motor'}
          className={`rounded-xl px-3.5 py-1.5 text-xs font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 ${
            motorLigado ? 'bg-red-600 text-white' : 'bg-primary text-white'
          }`}
        >
          {ocupado === 'motor' ? '…' : motorLigado ? 'Desligar tudo' : 'Ligar de novo'}
        </button>
      </section>

      {/* ─── Novo fluxo ──────────────────────────────────────────────────── */}
      {criando && (
        <div className="mb-4 rounded-2xl bg-white p-4 ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
            Nome do fluxo
          </label>
          <div className="mt-1 flex flex-wrap gap-2">
            <input
              autoFocus
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void criar()}
              placeholder="Boas-vindas fora do horário"
              className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
            />
            <button
              onClick={() => void criar()}
              disabled={!nome.trim() || ocupado === 'novo'}
              className="rounded-xl bg-primary px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
            >
              Criar
            </button>
            <button
              onClick={() => { setCriando(false); setNome(''); }}
              className="rounded-xl border border-gray-200 px-3.5 py-1.5 text-xs font-medium text-gray-600 dark:border-slate-600 dark:text-slate-300"
            >
              Cancelar
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-gray-400 dark:text-slate-500">
            O fluxo nasce <strong>desativado</strong>. Ele só passa a escrever para cliente depois
            de publicado e ligado — nessa ordem.
          </p>
        </div>
      )}

      {/* ─── Fluxos ──────────────────────────────────────────────────────── */}
      {fluxos.length === 0 ? (
        <div className="rounded-2xl bg-white px-6 py-12 text-center ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
          <Workflow className="mx-auto mb-2 text-gray-300 dark:text-slate-600" size={26} />
          <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Nenhum fluxo ainda</p>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-snug text-gray-400 dark:text-slate-500">
            Um fluxo é uma sequência de passos que o sistema executa sozinho numa conversa —
            responder fora do horário, pedir o número da unidade, abrir chamado.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {fluxos.map((f) => {
            const r = porFluxo.get(f.id);
            const andando = (r?.ativos ?? 0) + (r?.aguardando ?? 0);
            return (
              <div
                key={f.id}
                className="flex flex-wrap items-center gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/inbox/fluxos/${f.id}`}
                      className="truncate text-sm font-semibold text-gray-800 hover:text-primary dark:text-white"
                    >
                      {f.nome}
                    </Link>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      f.ativo
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {f.ativo ? 'ligado' : 'desligado'}
                    </span>
                  </div>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-gray-500 dark:text-slate-400">
                    {andando > 0 && <span className="font-medium text-sky-600 dark:text-sky-400">{andando} em andamento</span>}
                    {(r?.concluidos ?? 0) > 0 && <span>{r?.concluidos} concluídos</span>}
                    {(r?.falhados ?? 0) > 0 && (
                      <span className="font-medium text-red-600 dark:text-red-400">{r?.falhados} falharam</span>
                    )}
                    {(r?.interrompidos ?? 0) > 0 && <span>{r?.interrompidos} interrompidos</span>}
                    {r?.ultimo_em && <span>último {desdeQuando(r.ultimo_em)}</span>}
                    {!r?.ultimo_em && <span>nunca rodou</span>}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    onClick={() => void alternar(f)}
                    disabled={ocupado === f.id}
                    className={`rounded-xl px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                      f.ativo
                        ? 'border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 dark:border-slate-600 dark:text-slate-300'
                        : 'bg-primary/10 text-primary hover:bg-primary/20'
                    }`}
                  >
                    {f.ativo ? 'Desligar' : 'Ligar'}
                  </button>
                  <Link
                    to={`/inbox/fluxos/${f.id}`}
                    className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-600 dark:text-slate-300"
                  >
                    Abrir
                  </Link>
                  <button
                    onClick={() => void remover(f)}
                    disabled={ocupado === f.id}
                    title="Apagar fluxo"
                    className="rounded-xl p-1.5 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Onde parou mal ──────────────────────────────────────────────── */}
      <section className="mt-6">
        <h2 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-gray-800 dark:text-white">
          <Zap size={14} className="text-secondary" />
          Execuções que pararam mal
        </h2>
        {problematicos.length === 0 ? (
          <p className="flex items-center gap-1.5 rounded-2xl bg-white px-4 py-3 text-xs text-gray-500 ring-1 ring-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
            <CheckCircle2 size={13} className="text-emerald-500" />
            Nenhuma falha nem interrupção registrada.
          </p>
        ) : (
          <div className="space-y-1.5">
            {problematicos.map((run) => (
              <div
                key={run.id}
                className="rounded-xl bg-white px-3.5 py-2 ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700"
              >
                <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-slate-200">
                  <AlertTriangle
                    size={12}
                    className={run.status === 'falhou' ? 'text-red-500' : 'text-secondary'}
                  />
                  {nomePorFluxo.get(run.flow_id) ?? 'fluxo removido'}
                  <span className="font-normal text-gray-400 dark:text-slate-500">
                    · passo {run.current_node ?? '—'} · {desdeQuando(run.updated_at)}
                  </span>
                </p>
                {/* O motivo é escrito em português pelo motor (regra R2 da Fase
                    9). Um run parado sem motivo é indistinguível de um travado. */}
                <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-slate-400">
                  {run.motivo_parada ?? 'Sem motivo registrado.'}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
